'use client';

import { motion } from "motion/react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../ui";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../app/components/ui/sheet";
import { useIsMobile } from "@/lib/useBreakpoint";
import type { SidebarItem } from "./types";

interface SidebarProps {
  items: SidebarItem[];
  activePath?: string;
  isCollapsed: boolean;
  onToggle: () => void;
  renderItem?: (
    item: SidebarItem,
    itemClassName: string,
    content: ReactNode,
    isActive: boolean,
  ) => ReactNode;
  /** Mobile off-canvas; controlled by MainLayout (hamburger in Header). */
  mobileNavOpen: boolean;
  onMobileNavOpenChange: (open: boolean) => void;
}

function isActiveItem(item: SidebarItem, activePath?: string) {
  if (!activePath) return false;
  if (item.href && item.href === activePath) return true;
  if (item.children?.length) return item.children.some((c) => isActiveItem(c, activePath));
  return false;
}

function SidebarRow({
  item,
  isCollapsed,
  activePath,
  renderItem,
  depth,
  itemKey,
  expandedKeys,
  onToggleGroup,
}: {
  item: SidebarItem;
  isCollapsed: boolean;
  activePath?: string;
  renderItem?: SidebarProps["renderItem"];
  depth: number;
  itemKey: string;
  expandedKeys: Set<string>;
  onToggleGroup: (key: string) => void;
}) {
  const hasChildren = Boolean(item.children?.length);
  const isActive = isActiveItem(item, activePath);
  const isGroup = hasChildren && !item.href;
  const expanded = isGroup ? expandedKeys.has(itemKey) || isActive : false;

  const itemClassName = cn(
    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative min-h-9 [@media(pointer:coarse)]:min-h-11",
    depth > 0 && "ml-4 py-2",
    isActive
      ? "bg-primary text-primary-foreground shadow-sm"
      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
  );

  const content = (
    <>
      <div className="flex items-center justify-between gap-2 w-full min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <item.icon className={cn("flex-shrink-0", depth > 0 ? "w-4 h-4" : "w-5 h-5")} />
          </motion.div>
          <motion.span
            initial={false}
            animate={{
              opacity: isCollapsed ? 0 : 1,
              width: isCollapsed ? 0 : "auto",
            }}
            transition={{ duration: 0.2 }}
            className={cn("whitespace-nowrap overflow-hidden", depth > 0 && "text-sm")}
          >
            {item.label}
          </motion.span>
        </div>

        {isGroup && !isCollapsed ? (
          <span className="text-muted-foreground/60 shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
        ) : null}
      </div>
      {isCollapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
          {item.label}
        </div>
      )}
    </>
  );

  const row =
    item.href && renderItem ? (
      renderItem(item, itemClassName, content, isActive)
    ) : item.href ? (
      <a href={item.href} className={itemClassName}>
        {content}
      </a>
    ) : isGroup ? (
      <button
        type="button"
        onClick={() => onToggleGroup(itemKey)}
        className={cn(itemClassName, "w-full text-left")}
      >
        {content}
      </button>
    ) : (
      <div className={cn(itemClassName, "cursor-default")}>{content}</div>
    );

  return (
    <div className="space-y-1">
      <div>{row}</div>
      {hasChildren ? (
        <div className={cn("space-y-1", (isCollapsed || (isGroup && !expanded)) && "hidden")}>
          {item.children!.map((child) => (
            <SidebarRow
              key={child.href ?? `${itemKey}-${child.label}`}
              item={child}
              isCollapsed={isCollapsed}
              activePath={activePath}
              renderItem={renderItem}
              depth={depth + 1}
              itemKey={child.href ?? `${itemKey}/${child.label}`}
              expandedKeys={expandedKeys}
              onToggleGroup={onToggleGroup}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SidebarBrandRow({
  isCollapsed,
  onToggle,
  showCollapseButton,
}: {
  isCollapsed: boolean;
  onToggle: () => void;
  showCollapseButton: boolean;
}) {
  return (
    <div className="h-16 flex items-center justify-between px-4 border-b border-sidebar-border shrink-0 bg-sidebar">
      <motion.div
        initial={false}
        animate={{ opacity: isCollapsed ? 0 : 1 }}
        transition={{ duration: 0.2 }}
        className="flex items-center gap-2 min-w-0"
      >
        <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 ring-1 ring-border bg-background">
          <img
            src="/logo.png"
            alt=""
            width={32}
            height={32}
            className="w-full h-full object-cover"
          />
        </div>
        {!isCollapsed && (
          <span className="font-semibold text-lg text-foreground truncate">
            UpLevo POS
          </span>
        )}
      </motion.div>
      {showCollapseButton ? (
        <motion.button
          type="button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onToggle}
          className="w-8 h-8 shrink-0 rounded-lg hover:bg-accent flex items-center justify-center transition-colors"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft
            className={cn(
              "w-5 h-5 text-muted-foreground transition-transform",
              isCollapsed && "rotate-180",
            )}
          />
        </motion.button>
      ) : (
        <span className="w-8 shrink-0" aria-hidden />
      )}
    </div>
  );
}

function SidebarNavList({
  items,
  isCollapsed,
  activePath,
  renderItem,
  expandedKeys,
  onToggleGroup,
}: {
  items: SidebarItem[];
  isCollapsed: boolean;
  activePath?: string;
  renderItem?: SidebarProps["renderItem"];
  expandedKeys: Set<string>;
  onToggleGroup: (key: string) => void;
}) {
  return (
    <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1">
      {items.map((item) => (
        <SidebarRow
          key={item.href ?? item.label}
          item={item}
          isCollapsed={isCollapsed}
          activePath={activePath}
          renderItem={renderItem}
          depth={0}
          itemKey={item.href ?? item.label}
          expandedKeys={expandedKeys}
          onToggleGroup={onToggleGroup}
        />
      ))}
    </nav>
  );
}

export function Sidebar({
  items,
  activePath,
  isCollapsed,
  onToggle,
  renderItem,
  mobileNavOpen,
  onMobileNavOpenChange,
}: SidebarProps) {
  const isMobile = useIsMobile();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());
  const prevActivePathRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevActivePathRef.current !== undefined && prevActivePathRef.current !== activePath) {
      onMobileNavOpenChange(false);
    }
    prevActivePathRef.current = activePath;
  }, [activePath, onMobileNavOpenChange]);

  const toggleGroup = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const navBody = (
    <div className="flex h-full min-h-0 flex-col bg-sidebar text-sidebar-foreground">
      <SidebarBrandRow
        isCollapsed={isMobile ? false : isCollapsed}
        onToggle={onToggle}
        showCollapseButton={!isMobile}
      />
      <SidebarNavList
        items={items}
        isCollapsed={isMobile ? false : isCollapsed}
        activePath={activePath}
        renderItem={renderItem}
        expandedKeys={expandedKeys}
        onToggleGroup={toggleGroup}
      />
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={mobileNavOpen} onOpenChange={onMobileNavOpenChange}>
        <SheetContent side="left" className="w-[min(100vw-2rem,20rem)] p-0 border-sidebar-border">
          <SheetHeader className="sr-only">
            <SheetTitle>Main menu</SheetTitle>
            <SheetDescription>Navigate the application</SheetDescription>
          </SheetHeader>
          {navBody}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? 80 : 256 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="hidden md:flex bg-sidebar border-r border-sidebar-border flex-col h-screen sticky top-0 text-sidebar-foreground shrink-0"
    >
      {navBody}
    </motion.aside>
  );
}
